import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import { useCountry } from '../contexts/CountryContext';
import { useAuth } from '../contexts/AuthContext';
import { useSEO, buildFaqSchema, buildBreadcrumbSchema, buildServiceSchema } from '../utils/seo';
import {
  Check, X, TrendingDown, Sparkles, Zap, Gift, Users, MessageCircle,
  ArrowRight, Building2, Target, ShieldCheck, Rocket, Crown, Trophy,
  Star, Copy as CopyIcon
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { AnimatedNumber } from '../hooks/useCountUp';
import Confetti from '../components/Confetti';
import LeadCaptureModal from '../components/LeadCaptureModal';
import AgentLeaderboard from '../components/AgentLeaderboard';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/** Country-specific value propositions for agents */
const COUNTRY_CONTENT = {
  UK: {
    flag: '🇬🇧',
    label: 'United Kingdom',
    currency: '£',
    competitor: 'Rightmove',
    competitor_cost: '£8,388',
    competitor_monthly: '£699',
    our_cost: '£99',
    our_monthly: '£99',
    saving_pct: '86%',
    annual_saving: '£7,200',
    hero_headline: 'Grow your estate agency with Shopperden.',
    hero_sub: 'List unlimited properties from £99/month, with AI tools, ID-verified leads and a rewards programme that helps your business grow. Sit alongside direct sellers — buyers can choose either, and ID-verified agents get priority placement.',
    stats: [
      { label: 'Monthly subscription', value: 'From £99' },
      { label: 'AI listing descriptions', value: 'Unlimited' },
      { label: 'Average lead response time', value: 'Under 1 hour' },
    ],
    competitors: [
      { name: 'Rightmove', cost: '£699/mo', listings: '50', ai: false, leads: 'Generic', crm: false, viral: false },
      { name: 'Zoopla', cost: '£289/mo', listings: '25', ai: false, leads: 'Generic', crm: false, viral: false },
      { name: 'OnTheMarket', cost: '£199/mo', listings: '20', ai: false, leads: 'Generic', crm: false, viral: false },
      { name: 'Shopperden', cost: '£99/mo', listings: 'Unlimited', ai: true, leads: 'AI-scored', crm: true, viral: true, highlight: true },
    ],
    testimonial_roles: 'Estate agents, letting agents, property managers',
    share_text: 'Found Shopperden — an AI-powered UK property platform. Unlimited listings from £99/month and buyers can see both agent and direct listings:',
  },
  IN: {
    flag: '🇮🇳',
    label: 'India',
    currency: '₹',
    competitor: '99acres',
    competitor_cost: '₹42,000',
    competitor_monthly: '₹3,500',
    our_cost: '₹9,999',
    our_monthly: '₹999',
    saving_pct: '76%',
    annual_saving: '₹32,000',
    hero_headline: 'Grow your property business with Shopperden.',
    hero_sub: 'List unlimited properties from ₹999/month with a Hindi + English AI listing writer, WhatsApp lead alerts and a referral rewards programme built for Indian agents. Buyers can choose agent or direct listings — verified agents are featured first.',
    stats: [
      { label: 'Monthly subscription', value: 'From ₹999' },
      { label: 'AI listings (Hindi & English)', value: 'Unlimited' },
      { label: 'WhatsApp lead alerts', value: 'Instant' },
    ],
    competitors: [
      { name: '99acres', cost: '₹3,500/mo', listings: '30', ai: false, leads: 'Bulk', crm: false, viral: false },
      { name: 'MagicBricks', cost: '₹2,999/mo', listings: '25', ai: false, leads: 'Generic', crm: false, viral: false },
      { name: 'Housing.com', cost: '₹2,499/mo', listings: '20', ai: false, leads: 'Generic', crm: false, viral: false },
      { name: 'Shopperden', cost: '₹999/mo', listings: 'Unlimited', ai: true, leads: 'AI-scored', crm: true, viral: true, highlight: true },
    ],
    testimonial_roles: 'RERA agents, property consultants, developers',
    share_text: 'Shopperden पर try करो — AI से Hindi/English listings, ₹999/mo से unlimited, और buyer agent या direct दोनों choose कर सकता है:',
  },
  CA: {
    flag: '🇨🇦',
    label: 'Canada',
    currency: 'C$',
    competitor: 'Realtor.ca',
    competitor_cost: 'C$12,000',
    competitor_monthly: 'C$1,000',
    our_cost: 'C$179',
    our_monthly: 'C$179',
    saving_pct: '82%',
    annual_saving: 'C$9,852',
    hero_headline: 'Grow your real-estate business with Shopperden.',
    hero_sub: 'List unlimited properties from C$179/month with AI tools, ID-verified leads and agent-to-agent rewards. Independent agents, brokerages and teams are all welcome. Buyers can choose an agent or a direct listing — verified agents are featured first.',
    stats: [
      { label: 'Monthly subscription', value: 'From C$179' },
      { label: 'Independent + brokerage agents', value: 'All welcome' },
      { label: 'AI listing descriptions', value: 'Unlimited' },
    ],
    competitors: [
      { name: 'Realtor.ca', cost: 'CREA-gated', listings: 'Gated', ai: false, leads: 'Generic', crm: false, viral: false },
      { name: 'Zolo', cost: 'C$249/mo', listings: '30', ai: false, leads: 'Generic', crm: false, viral: false },
      { name: 'HouseSigma', cost: 'C$199/mo', listings: '25', ai: false, leads: 'Generic', crm: false, viral: false },
      { name: 'Shopperden', cost: 'C$179/mo', listings: 'Unlimited', ai: true, leads: 'AI-scored', crm: true, viral: true, highlight: true },
    ],
    testimonial_roles: 'Independent agents, brokerage teams, property managers',
    share_text: 'Try Shopperden — an AI-powered Canadian property platform from C$179/month, with both independent and brokerage agents welcome:',
  },
};

const FEATURES = [
  { icon: Sparkles, title: 'AI Listing Writer', desc: 'Paste an address and our AI writes a portal-quality description in seconds. Unlimited.' },
  { icon: Target, title: 'AI Lead Scoring', desc: 'Every buyer enquiry is tagged Hot, Warm or Cold with reasons, so you can call the right buyers first.' },
  { icon: Zap, title: 'Instant WhatsApp & Email Alerts', desc: 'New lead? Full context arrives in your WhatsApp within 30 seconds — be the first to reply.' },
  { icon: TrendingDown, title: 'Live Market Snapshot', desc: 'An auto-generated weekly report per postcode you can share with vendors to win new instructions.' },
  { icon: ShieldCheck, title: 'ID-Verified Buyers', desc: 'Every buyer is ID-checked before they can message you. No fake enquiries, no time wasted.' },
  { icon: Building2, title: '3D Tours + AI Floor Plans', desc: 'Upload photos and we generate professional floor plans in architect style — helping your listings stand out.' },
  { icon: Trophy, title: 'Ambassador Rewards Programme', desc: 'A seven-tier ladder — refer fellow agents to unlock priority listing boosts, premium themes and Founder\'s Lounge access.' },
  { icon: Gift, title: 'No commission. No lock-in.', desc: 'A fixed monthly subscription with no hidden fees. Your commission from vendors stays 100% yours.' },
];

const ForAgentsPage = () => {
  const { country: userCountry } = useCountry();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlCountry = (searchParams.get('c') || '').toUpperCase();
  const defaultCountry = COUNTRY_CONTENT[urlCountry] ? urlCountry : (COUNTRY_CONTENT[userCountry] ? userCountry : 'UK');
  const [activeCountry, setActiveCountry] = useState(defaultCountry);
  const [viralCode, setViralCode] = useState(null);
  const [publicStats, setPublicStats] = useState(null);
  const [confettiTick, setConfettiTick] = useState(0);
  const [showCaptureModal, setShowCaptureModal] = useState(false);
  const [exitIntentFired, setExitIntentFired] = useState(false);

  const c = COUNTRY_CONTENT[activeCountry];

  // Country-aware SEO copy. Title/description are tuned for the
  // "estate agent looking for a Rightmove/99acres/Realtor.ca alternative"
  // long-tail query — the highest-intent prospect for this page.
  // Canonical points to the bare /for-agents URL so Google consolidates
  // ranking signal across UK/IN/CA query variants (the page is the same
  // product, just with localised numbers).
  const seoCopy = {
    UK: {
      title: 'For Estate Agents · Unlimited listings from £99/mo · AI lead scoring',
      description: 'UK estate agents — list unlimited properties from £99/month with AI lead scoring, ID-verified buyers, WhatsApp alerts and a Rightmove-comparable feature set at 86% lower cost.',
    },
    IN: {
      title: 'For Property Agents in India · Unlimited listings from ₹999/mo · Hindi + English AI',
      description: 'RERA-verified property agents — list unlimited properties from ₹999/month with Hindi + English AI listing writer, instant WhatsApp lead alerts, and 76% lower cost vs 99acres.',
    },
    CA: {
      title: 'For Real-Estate Agents in Canada · Unlimited listings from C$179/mo · AI tools',
      description: 'Independent agents and brokerage teams — list unlimited properties from C$179/month with AI listing writer, ID-verified leads and 82% lower cost vs Realtor.ca-style platforms.',
    },
  }[activeCountry] || {
    title: `For Agents — Grow your business from ${c.our_monthly}/month`,
    description: `${c.hero_headline} ${c.hero_sub.slice(0, 140)}`,
  };

  // Country-specific FAQ keyed for the "is this for me / how does this
  // beat my current portal" decision moment. Surfaces as rich-result
  // accordions on Google for high-intent agent queries.
  const faqItems = [
    {
      q: `How does Shopperden compare to ${c.competitor} for ${COUNTRY_CONTENT[activeCountry].label} agents?`,
      a: `Shopperden gives ${COUNTRY_CONTENT[activeCountry].label} estate agents unlimited listings from ${c.our_monthly}/month vs ${c.competitor}'s ${c.competitor_monthly}/month. That's a ${c.saving_pct} saving (${c.annual_saving} per year), plus AI listing writer, AI lead scoring, WhatsApp alerts, and ID-verified buyers — none of which ${c.competitor} provides.`,
    },
    {
      q: 'Do I need a long-term contract or commitment?',
      a: 'No. Shopperden is a fixed monthly subscription with no lock-in, no commission on completed sales, and no setup fees. You can cancel anytime and your commission from vendors stays 100% yours.',
    },
    {
      q: 'How do you stop unqualified leads from wasting my time?',
      a: 'Every buyer is ID-verified before they can message an agent. Our AI then tags each enquiry Hot / Warm / Cold with reasoning, and pushes the Hot leads to your WhatsApp within 30 seconds so you can be first to respond.',
    },
    {
      q: 'Can independent agents and brokerage teams both join?',
      a: 'Yes. Solo agents, brokerage teams and property managers are all welcome. RERA, RICS and CREA-verified agents get a verified badge and priority placement in buyer search results.',
    },
    {
      q: 'What AI tools do I actually get?',
      a: 'Unlimited AI-written listing descriptions (portal-quality, in seconds), AI floor-plan generation from photos, AI lead scoring, a weekly market-snapshot report per postcode you can share with vendors to win new instructions, and AI-powered WhatsApp scripts.',
    },
  ];

  useSEO({
    title: seoCopy.title,
    description: seoCopy.description,
    canonical: 'https://shopperden.com/for-agents',
    image: 'https://shopperden.com/og-image.png',
    schema: [
      buildBreadcrumbSchema([
        { name: 'Home', url: '/' },
        { name: 'For Estate Agents', url: '/for-agents' },
      ]),
      buildServiceSchema({
        name: 'Shopperden for Estate Agents',
        description: seoCopy.description,
        url: 'https://shopperden.com/for-agents',
        areaServed: ['GB', 'IN', 'CA'],
      }),
      buildFaqSchema(faqItems),
    ],
  });

  // Track page-view once on mount (and whenever country changes via URL)
  useEffect(() => {
    const source = searchParams.get('src') ? `vs-${searchParams.get('src')}` : 'for-agents';
    axios.post(`${API_URL}/api/sales/track-lead`, {
      source, country: activeCountry, cta: 'page-view',
    }).catch(() => {});
  }, [activeCountry, searchParams]);

  // Load FOMO counters
  useEffect(() => {
    axios.get(`${API_URL}/api/sales/public-stats`)
      .then(res => setPublicStats(res.data))
      .catch(() => {});
  }, []);

  // Exit-intent: mouse leaves top of viewport → show capture modal (once per session, desktop only)
  useEffect(() => {
    if (user) return; // no capture for already-logged-in users
    if (sessionStorage.getItem('lead-capture-shown') === '1') return;

    const handler = (e) => {
      // Only trigger when mouse leaves via top of screen on desktop
      if (e.clientY <= 0 && !exitIntentFired && window.innerWidth >= 768) {
        setExitIntentFired(true);
        setShowCaptureModal(true);
        sessionStorage.setItem('lead-capture-shown', '1');
      }
    };
    document.addEventListener('mouseleave', handler);
    return () => document.removeEventListener('mouseleave', handler);
  }, [user, exitIntentFired]);

  // If logged in agent, fetch their viral code for direct share
  useEffect(() => {
    if (user?.role === 'agent') {
      axios.get(`${API_URL}/api/viral/me`)
        .then(res => setViralCode(res.data))
        .catch(() => {});
    }
  }, [user]);

  const trackCta = (cta) => {
    const source = searchParams.get('src') ? `vs-${searchParams.get('src')}` : 'for-agents';
    axios.post(`${API_URL}/api/sales/track-lead`, {
      source, country: activeCountry, cta,
    }).catch(() => {});
  };

  const handleCountryChange = (code) => {
    setActiveCountry(code);
    const next = new URLSearchParams(searchParams);
    next.set('c', code);
    setSearchParams(next, { replace: true });
    setConfettiTick(t => t + 1); // 🎉 playful confetti burst
  };

  // WhatsApp-optimised share URL with pre-filled agent text
  const buildShareLink = (platform) => {
    const pageUrl = viralCode?.share_link || `${window.location.origin}/for-agents?c=${activeCountry}`;
    const msg = `${c.share_text} ${pageUrl}`;
    const encoded = encodeURIComponent(msg);
    const url = encodeURIComponent(pageUrl);
    const links = {
      whatsapp: `https://wa.me/?text=${encoded}`,
      telegram: `https://t.me/share/url?url=${url}&text=${encodeURIComponent(c.share_text)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      twitter: `https://twitter.com/intent/tweet?text=${encoded}`,
      email: `mailto:?subject=${encodeURIComponent(`${c.competitor} alternative — Shopperden`)}&body=${encoded}`,
    };
    return links[platform];
  };

  const copyShareText = () => {
    const pageUrl = viralCode?.share_link || `${window.location.origin}/for-agents?c=${activeCountry}`;
    const msg = `${c.share_text} ${pageUrl}`;
    navigator.clipboard.writeText(msg);
    toast.success('🎉 WhatsApp-ready text copied!');
    trackCta('copy-text');
    setConfettiTick(t => t + 1);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="for-agents-page">
      <Header />
      <Confetti trigger={confettiTick} />
      <LeadCaptureModal
        isOpen={showCaptureModal}
        onClose={() => setShowCaptureModal(false)}
        country={activeCountry}
        source={searchParams.get('src') ? `vs-${searchParams.get('src')}` : 'for-agents'}
      />

      {/* FOMO banner — "X agents joined this week" — always reserves height to prevent layout shift */}
      <div className="bg-[#E07A3A] text-white text-xs sm:text-sm min-h-[36px]" data-testid="fomo-banner">
        {publicStats && (publicStats.agents_this_week > 0 || publicStats.founding_slots_remaining < 100) ? (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2 flex items-center justify-center gap-3">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-white fomo-dot" />
            </span>
            <span className="tracking-wider uppercase font-semibold">
              {publicStats.founding_slots_remaining > 0
                ? `${publicStats.founding_slots_remaining}/${publicStats.founding_total} founding agent slots remaining`
                : 'Founding programme full — next cohort opening soon'}
            </span>
            {publicStats.agents_this_week > 0 && (
              <>
                <span className="hidden sm:inline opacity-50">·</span>
                <span className="hidden sm:inline tracking-wider uppercase font-semibold">
                  {publicStats.agents_this_week} agent{publicStats.agents_this_week === 1 ? '' : 's'} joined this week
                </span>
              </>
            )}
          </div>
        ) : null}
      </div>

      {/* Country Tabs */}
      <div className="sticky top-[73px] bg-white border-b border-slate-200 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-2 overflow-x-auto">
          <span className="text-[10px] text-slate-400 tracking-[0.2em] uppercase font-bold flex-shrink-0 mr-2">Your Market:</span>
          {Object.entries(COUNTRY_CONTENT).map(([code, data]) => (
            <button
              key={code}
              onClick={() => handleCountryChange(code)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold tracking-wider uppercase transition-all whitespace-nowrap ${
                activeCountry === code
                  ? 'bg-[#0F172A] text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
              data-testid={`country-tab-${code}`}
            >
              <span className="text-base">{data.flag}</span>
              {data.label}
            </button>
          ))}
        </div>
      </div>

      {/* HERO */}
      <section className="bg-[#0F172A] text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(224,122,58,0.15),transparent_50%)] pointer-events-none" />
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.07] pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)',
            backgroundSize: '28px 28px',
          }}
        />
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-16 sm:py-24 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#E07A3A]/15 border border-[#E07A3A]/40 text-[#FED7AA] text-[11px] tracking-[0.28em] uppercase font-bold mb-6 backdrop-blur-sm">
                <Sparkles className="h-3 w-3" />
                For Agents · {c.label}
              </div>
              <h1 className="font-['Playfair_Display'] font-semibold leading-[1.05] tracking-tight mb-6 text-white text-[2.25rem] sm:text-5xl lg:text-[3.75rem]">
                <span className="block">{c.hero_headline.split('.')[0]}.</span>
                <span className="block text-[#E07A3A] italic">{c.hero_headline.split('.').slice(1).join('.').trim() || 'List smarter.'}</span>
              </h1>
              <p className="text-white/65 text-[15px] sm:text-lg leading-relaxed mb-8 max-w-xl">{c.hero_sub}</p>

              <div className="flex flex-col sm:flex-row gap-3 mb-10">
                <Link
                  to="/register?role=agent"
                  onClick={() => trackCta('register')}
                  className="inline-flex items-center justify-center gap-2 bg-[#E07A3A] hover:bg-[#C4652E] text-white px-8 py-4 tracking-widest uppercase font-bold text-sm transition-all hover:scale-105 active:scale-95"
                  data-testid="cta-register-agent"
                >
                  Start Free 90 Days <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href={buildShareLink('whatsapp')}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackCta('share-whatsapp')}
                  className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1EB255] text-white px-6 py-4 tracking-widest uppercase font-bold text-sm transition-all hover:scale-105 active:scale-95"
                  data-testid="cta-share-whatsapp-hero"
                >
                  <MessageCircle className="h-4 w-4" /> Share with Agents
                </a>
              </div>

              {/* Stats band with animated numbers */}
              <div className="grid grid-cols-3 gap-4 pt-8 border-t border-white/10" key={`stats-${activeCountry}`}>
                {c.stats.map((s) => (
                  <div key={s.label}>
                    <p className="font-['Playfair_Display'] text-2xl sm:text-3xl text-[#E07A3A] font-semibold">
                      <AnimatedNumber value={s.value} />
                    </p>
                    <p className="text-[10px] text-white/40 tracking-[0.2em] uppercase mt-1">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: Savings calculator with animated amount */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-8" data-testid="savings-card" key={`sav-${activeCountry}`}>
              <p className="text-[10px] text-[#E07A3A] tracking-[0.25em] uppercase font-bold mb-3">Savings Calculator</p>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-white/10">
                  <div>
                    <p className="text-sm text-white/50">You currently pay</p>
                    <p className="text-xs text-white/40">{c.competitor} annual cost</p>
                  </div>
                  <p className="font-['Playfair_Display'] text-2xl text-white/70 line-through">{c.competitor_cost}</p>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-white/10">
                  <div>
                    <p className="text-sm text-white/50">Shopperden annual cost</p>
                    <p className="text-xs text-white/40">Unlimited listings + AI tools</p>
                  </div>
                  <p className="font-['Playfair_Display'] text-2xl text-[#E07A3A] font-bold">{c.our_cost === c.our_monthly ? `${c.our_monthly}/mo` : c.our_cost}</p>
                </div>
                <div className="bg-[#E07A3A]/10 border border-[#E07A3A]/40 p-5 flex items-center justify-between">
                  <div>
                    <p className="text-[10px] text-[#E07A3A] tracking-[0.2em] uppercase font-bold">Your Annual Saving</p>
                    <p className="text-xs text-white/50 mt-1">{c.saving_pct} cheaper than {c.competitor}</p>
                  </div>
                  <p className="font-['Playfair_Display'] text-3xl sm:text-4xl text-[#E07A3A] font-bold" data-testid="animated-saving">
                    <AnimatedNumber value={c.annual_saving} duration={1800} />
                  </p>
                </div>

                {/* Email-me-the-breakdown button */}
                {!user && (
                  <button
                    onClick={() => setShowCaptureModal(true)}
                    className="w-full mt-2 border border-white/20 hover:border-[#E07A3A] hover:text-[#E07A3A] text-white/80 py-3 text-xs tracking-widest uppercase font-bold transition-colors flex items-center justify-center gap-2"
                    data-testid="email-me-breakdown-btn"
                  >
                    📬 Email me this breakdown
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW BUYERS CHOOSE YOU — agent advantage section */}
      <section className="bg-white border-b border-slate-100" data-testid="buyer-choice-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <p className="text-[11px] text-[#E07A3A] tracking-[0.3em] uppercase font-bold mb-3">How buyers choose you</p>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl text-[#0F172A] mb-4">
              Direct listings and agent listings, <span className="italic text-[#E07A3A]">side by side.</span>
            </h2>
            <p className="text-slate-500 leading-relaxed">
              Shopperden buyers can browse both direct-seller and agent listings — and the vast majority <b>still prefer an agent</b> for valuation advice, negotiation, viewings, and paperwork.
              As a verified Shopperden agent, you appear with your profile, reviews, response time and track record on every enquiry you take — giving you a clear edge.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4 mb-10">
            {[
              { icon: ShieldCheck, title: 'Verified-agent badge', desc: 'Your ID, licence and track record are shown alongside every listing. Buyers trust agents first — and Shopperden makes that trust visible.' },
              { icon: Target, title: 'Priority placement', desc: 'Verified agents are featured above unverified direct listings in search results and buyer match notifications.' },
              { icon: Users, title: 'Direct sellers come to you', desc: 'Direct sellers who hit the limits of DIY often ask Shopperden to connect them with a local agent. Verified agents get first refusal.' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="p-6 bg-[#F8FAFC] border border-slate-200 hover:border-[#E07A3A]/40 transition-colors" data-testid={`agent-advantage-${title.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="w-10 h-10 bg-[#FEF3EC] border border-[#E07A3A]/30 flex items-center justify-center mb-4">
                  <Icon className="h-5 w-5 text-[#E07A3A]" />
                </div>
                <h3 className="font-semibold text-[#0F172A] mb-2 text-base">{title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          {/* Live leaderboard — creates FOMO + social proof */}
          <div className="max-w-2xl mx-auto">
            <AgentLeaderboard country={activeCountry} limit={5} />
          </div>
        </div>
      </section>

      {/* FEATURES GRID */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <p className="text-[11px] text-[#E07A3A] tracking-[0.3em] uppercase font-bold mb-3">Why agents choose Shopperden</p>
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl text-[#0F172A] mb-4">
            Built for <span className="italic text-[#E07A3A]">{c.testimonial_roles.split(',')[0].toLowerCase()}</span>, powered by modern tools.
          </h2>
          <p className="text-slate-500">Traditional portals give you a listing board. Shopperden gives you a complete agency toolkit — so you can spend less time admin-ing and more time winning instructions.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-l border-slate-200" data-testid="feature-grid" key={`feat-${activeCountry}`}>
          {FEATURES.map(({ icon: Icon, title, desc }) => (
            <div key={title} className="stagger-in p-6 border-r border-b border-slate-200 hover:bg-slate-50 hover:-translate-y-0.5 transition-all cursor-default">
              <div className="w-10 h-10 bg-[#FEF3EC] border border-[#E07A3A]/30 flex items-center justify-center mb-4">
                <Icon className="h-5 w-5 text-[#E07A3A]" />
              </div>
              <h3 className="font-semibold text-[#0F172A] mb-2 text-base">{title}</h3>
              <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="bg-[#F8FAFC] py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-[11px] text-[#E07A3A] tracking-[0.3em] uppercase font-bold mb-3">The honest comparison</p>
            <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl text-[#0F172A]">
              Shopperden vs {c.label} competitors
            </h2>
          </div>

          <div className="overflow-x-auto bg-white border border-slate-200" data-testid="competitor-table">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="bg-[#0F172A] text-white">
                  <th className="py-4 px-4 text-left text-[10px] tracking-[0.2em] uppercase font-bold">Platform</th>
                  <th className="py-4 px-4 text-left text-[10px] tracking-[0.2em] uppercase font-bold">Monthly</th>
                  <th className="py-4 px-4 text-left text-[10px] tracking-[0.2em] uppercase font-bold">Listings</th>
                  <th className="py-4 px-4 text-center text-[10px] tracking-[0.2em] uppercase font-bold">AI Tools</th>
                  <th className="py-4 px-4 text-left text-[10px] tracking-[0.2em] uppercase font-bold">Leads</th>
                  <th className="py-4 px-4 text-center text-[10px] tracking-[0.2em] uppercase font-bold">CRM</th>
                  <th className="py-4 px-4 text-center text-[10px] tracking-[0.2em] uppercase font-bold">Agent Rewards</th>
                </tr>
              </thead>
              <tbody>
                {c.competitors.map((comp) => (
                  <tr
                    key={comp.name}
                    className={`border-b border-slate-100 last:border-0 ${comp.highlight ? 'bg-[#FEF3EC]' : ''}`}
                    data-testid={`comp-row-${comp.name.toLowerCase().replace(/[^a-z]/g, '')}`}
                  >
                    <td className={`py-4 px-4 font-semibold ${comp.highlight ? 'text-[#E07A3A]' : 'text-[#0F172A]'}`}>
                      {comp.name}
                      {comp.highlight && <span className="ml-2 text-[9px] bg-[#E07A3A] text-white px-1.5 py-0.5 tracking-wider uppercase">Best</span>}
                    </td>
                    <td className={`py-4 px-4 ${comp.highlight ? 'text-[#0F172A] font-bold' : 'text-slate-600'}`}>{comp.cost}</td>
                    <td className="py-4 px-4 text-slate-600">{comp.listings}</td>
                    <td className="py-4 px-4 text-center">{comp.ai ? <Check className="h-4 w-4 text-[#E07A3A] mx-auto" /> : <X className="h-4 w-4 text-slate-300 mx-auto" />}</td>
                    <td className="py-4 px-4 text-slate-600">{comp.leads}</td>
                    <td className="py-4 px-4 text-center">{comp.crm ? <Check className="h-4 w-4 text-[#E07A3A] mx-auto" /> : <X className="h-4 w-4 text-slate-300 mx-auto" />}</td>
                    <td className="py-4 px-4 text-center">{comp.viral ? <Check className="h-4 w-4 text-[#E07A3A] mx-auto" /> : <X className="h-4 w-4 text-slate-300 mx-auto" />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* AMBASSADOR PROGRAMME (viral integration) */}
      <section className="bg-[#0F172A] text-white py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[11px] text-[#E07A3A] tracking-[0.3em] uppercase font-bold mb-3">Ambassador Programme</p>
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl mb-5">
                Refer one agent. <span className="italic text-[#E07A3A]">Unlock real perks.</span>
              </h2>
              <p className="text-white/60 leading-relaxed mb-6">
                Unlike any other portal, Shopperden rewards you for bringing other agents.
                Every signup earns XP and climbs you up a 7-tier ladder of product perks — priority boosts,
                unlimited AI tools, custom themes, the "Property Insider" title, and exclusive Founder's Lounge access.
              </p>
              <div className="space-y-3 mb-8">
                {[
                  { tier: '1 invite', perk: 'Ambassador Badge on your profile', icon: Star },
                  { tier: '5 invites', perk: 'Priority Listing Boost (7 days)', icon: Rocket },
                  { tier: '15 invites', perk: 'Unlimited AI Floor Plans', icon: Zap },
                  { tier: '25 invites', perk: '"Property Insider" title + early access', icon: Crown },
                  { tier: '50 invites', perk: 'Legendary Ambassador + Founder\'s Lounge', icon: Trophy },
                ].map(({ tier, perk, icon: I }) => (
                  <div key={tier} className="flex items-center gap-3 py-2 border-b border-white/10 last:border-0">
                    <div className="w-8 h-8 bg-[#E07A3A]/20 border border-[#E07A3A]/40 flex items-center justify-center flex-shrink-0">
                      <I className="h-4 w-4 text-[#E07A3A]" />
                    </div>
                    <div className="flex-1">
                      <p className="font-mono text-xs text-[#E07A3A] tracking-wider uppercase">{tier}</p>
                      <p className="text-sm text-white/80">{perk}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Link
                to={user ? '/invite' : '/register?role=agent'}
                onClick={() => trackCta('view-ambassador')}
                className="inline-flex items-center gap-2 bg-[#E07A3A] hover:bg-[#C4652E] text-white px-6 py-3 tracking-widest uppercase font-bold text-xs transition-all hover:scale-105 active:scale-95"
                data-testid="cta-ambassador"
              >
                {user ? 'Open My Referral Dashboard' : 'Join & Start Earning'} <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            {/* Share-ready WhatsApp block */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 p-8" data-testid="whatsapp-share-card">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 bg-[#25D366] flex items-center justify-center">
                  <MessageCircle className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold">WhatsApp-Ready Message</p>
                  <p className="text-xs text-white/40">Copy & paste into your agent groups</p>
                </div>
              </div>

              <div className="bg-[#0F172A] border border-white/10 p-4 mb-4">
                <p className="text-sm text-white/80 leading-relaxed whitespace-pre-wrap">{c.share_text} {viralCode?.share_link || `${typeof window !== 'undefined' ? window.location.origin : ''}/for-agents?c=${activeCountry}`}</p>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-3">
                <button
                  onClick={copyShareText}
                  className="flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 text-white py-3 px-4 transition-colors text-xs tracking-widest uppercase font-bold"
                  data-testid="copy-share-text"
                >
                  <CopyIcon className="h-4 w-4" /> Copy Text
                </button>
                <a
                  href={buildShareLink('whatsapp')}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackCta('share-whatsapp')}
                  className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1EB255] text-white py-3 px-4 transition-all hover:scale-[1.02] active:scale-95 text-xs tracking-widest uppercase font-bold"
                  data-testid="open-whatsapp"
                >
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </a>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <a href={buildShareLink('telegram')} target="_blank" rel="noopener noreferrer"
                   className="text-center bg-white/5 hover:bg-[#229ED9] text-white py-2.5 text-[10px] tracking-widest uppercase font-bold transition-colors" data-testid="share-telegram">
                  Telegram
                </a>
                <a href={buildShareLink('linkedin')} target="_blank" rel="noopener noreferrer"
                   className="text-center bg-white/5 hover:bg-[#0A66C2] text-white py-2.5 text-[10px] tracking-widest uppercase font-bold transition-colors" data-testid="share-linkedin">
                  LinkedIn
                </a>
                <a href={buildShareLink('email')}
                   className="text-center bg-white/5 hover:bg-[#E07A3A] text-white py-2.5 text-[10px] tracking-widest uppercase font-bold transition-colors" data-testid="share-email">
                  Email
                </a>
              </div>

              {user?.role === 'agent' && viralCode && (
                <p className="mt-4 text-[10px] text-[#E07A3A] tracking-wider uppercase font-semibold">
                  Your personal code: <span className="font-mono">{viralCode.code}</span> · Every signup earns you XP
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-center">
        <h2 className="font-['Playfair_Display'] text-3xl sm:text-5xl text-[#0F172A] mb-5">
          Every day on {c.competitor} costs you <span className="italic text-[#E07A3A]">{c.annual_saving === c.competitor_cost ? c.competitor_cost : c.annual_saving}</span>.
        </h2>
        <p className="text-slate-500 text-lg mb-8 max-w-2xl mx-auto">
          Start your free 90-day trial today. No card required. Cancel anytime.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/register?role=agent"
            onClick={() => trackCta('register')}
            className="inline-flex items-center gap-2 bg-[#E07A3A] hover:bg-[#C4652E] text-white px-8 py-4 tracking-widest uppercase font-bold text-sm transition-all hover:scale-105 active:scale-95"
            data-testid="cta-footer-register"
          >
            Start Free 90 Days <ArrowRight className="h-4 w-4" />
          </Link>
          <a
            href={buildShareLink('whatsapp')}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackCta('share-whatsapp')}
            className="inline-flex items-center gap-2 border border-slate-300 hover:border-[#E07A3A] hover:text-[#E07A3A] text-slate-600 px-8 py-4 tracking-widest uppercase font-bold text-sm transition-all hover:scale-105 active:scale-95"
            data-testid="cta-footer-share"
          >
            <MessageCircle className="h-4 w-4" /> Share with agents
          </a>
        </div>
      </section>
    </div>
  );
};

export default ForAgentsPage;
